import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IRebateDocumentEmail, defaultValue } from 'app/shared/model/rebate-document-email.model';

const initialState: EntityState<IRebateDocumentEmail> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/rebateDocumentEmails';

// Actions

export const getEntities = createAsyncThunk('rebateDocumentEmail/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IRebateDocumentEmail[]>(requestUrl);
});

export const getEntitiesByBankId = createAsyncThunk(
  'rebateDocumentEmail/fetch_entity_list_by_bank_id',
  async (params: number | string | { bankId: number | string; commodityType?: string }) => {
    const bankId = typeof params === 'object' ? params.bankId : params;
    const commodityType = typeof params === 'object' ? params.commodityType : undefined;
    const requestUrl = `${apiUrl}/getEntitiesByBankId/${bankId}${commodityType ? `?commodityType=${commodityType}` : ''}`;
    return axios.get<IRebateDocumentEmail[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getEntitiesByRebateSettingId = createAsyncThunk(
  'rebateDocumentEmail/fetch_entity_list_by_rebate_setting_id',
  async (rebateSettingId: number | string) => {
    const requestUrl = `${apiUrl}/getEntitiesByRebateSettingId/${rebateSettingId}`;
    return axios.get<IRebateDocumentEmail[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getEntity = createAsyncThunk(
  'rebateDocumentEmail/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IRebateDocumentEmail>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'rebateDocumentEmail/create_entity',
  async (entity: IRebateDocumentEmail, thunkAPI) => {
    const result = await axios.post<IRebateDocumentEmail>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'rebateDocumentEmail/update_entity',
  async (entity: IRebateDocumentEmail, thunkAPI) => {
    const result = await axios.put<IRebateDocumentEmail>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'rebateDocumentEmail/partial_update_entity',
  async (entity: IRebateDocumentEmail, thunkAPI) => {
    const result = await axios.patch<IRebateDocumentEmail>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'rebateDocumentEmail/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IRebateDocumentEmail>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const RebateDocumentEmailSlice = createEntitySlice({
  name: 'rebateDocumentEmail',
  initialState,
  extraReducers(builder) {
    builder

      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getEntitiesByBankId, getEntitiesByRebateSettingId), (state, action) => {
        state.entities = action.payload.data;
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntitiesByBankId, getEntitiesByRebateSettingId, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = RebateDocumentEmailSlice.actions;

// Reducer
export default RebateDocumentEmailSlice.reducer;
